/**
 * Head metadata helpers for English pages (title, description, Open Graph, canonical, hreflang)
 * plus the shared BreadcrumbList schema. Bengali pages use `bnPageHead` in `src/lib/bn-head.ts`.
 */
import { SITE } from "@/content/site";

type SeoInput = {
  title: string;
  description: string;
  path: string; // e.g. "/", "/services"
  image?: string; // absolute URL or path under site
  type?: "website" | "article" | "profile";
  keywords?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noindex?: boolean;
  /** Short label shown above the OG title (e.g. "Case Study", "Blog"). Defaults to "Freelancer Tamal". */
  ogEyebrow?: string;
  /** Override the OG-image headline. Defaults to the page title (without the brand suffix). */
  ogTitle?: string;
  /**
   * Cross-language counterparts. Provide a path (e.g. "/bn/blog") or absolute URL.
   * `en` defaults to the current page; only set it if the English page lives at a
   * different path than `path`. `bn` should only be set when a real Bangla
   * counterpart exists — otherwise omit (don't point Google at a non-existent page).
   */
  alternates?: { en?: string; bn?: string };
};

export function buildSeo(input: SeoInput) {
  const url = `${SITE.url}${input.path === "/" ? "" : input.path}`;
  // Default to a dynamic per-page OG image generated on the edge.
  // Strip a trailing " | Freelancer Tamal" / " — Freelancer Tamal" suffix from the headline.
  const headline = (input.ogTitle ?? input.title)
    .replace(/\s*[|—–-]\s*Freelancer Tamal\s*$/i, "")
    .trim();
  const eyebrow = input.ogEyebrow ?? "Freelancer Tamal";
  const dynamicOg = `${SITE.url}/api/og/${encodeURIComponent(eyebrow)}/${encodeURIComponent(headline)}.png`;
  const image =
    input.image
      ? input.image.startsWith("http")
        ? input.image
        : `${SITE.url}${input.image.startsWith("/") ? "" : "/"}${input.image}`
      : dynamicOg;
  const type = input.type ?? "website";

  const toAbs = (p: string) =>
    p.startsWith("http") ? p : `${SITE.url}${p.startsWith("/") ? "" : "/"}${p}`;
  const enHref = input.alternates?.en ? toAbs(input.alternates.en) : url;
  const bnHref = input.alternates?.bn ? toAbs(input.alternates.bn) : null;

  const meta: Array<Record<string, string>> = [
    { title: input.title },
    { name: "description", content: input.description },
    { name: "author", content: input.author ?? SITE.fullName },
    {
      name: "robots",
      content: input.noindex
        ? "noindex, follow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    },
    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE.name },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:image:alt", content: input.title },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:locale", content: "en_US" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
    { name: "twitter:image", content: image },
    { name: "twitter:site", content: "@freelancertamal" },
    { name: "twitter:creator", content: "@freelancertamal" },
  ];

  if (input.keywords) meta.push({ name: "keywords", content: input.keywords });
  if (input.publishedTime)
    meta.push({ property: "article:published_time", content: input.publishedTime });
  if (input.modifiedTime)
    meta.push({ property: "article:modified_time", content: input.modifiedTime });
  if (type === "article")
    meta.push({ property: "article:author", content: input.author ?? SITE.fullName });

  const links = [
    { rel: "canonical", href: url },
    // hreflang is only emitted when a real Bangla counterpart exists — a
    // self-only cluster claims nothing and an x-default with no alternates
    // is noise for crawlers.
    ...(bnHref
      ? [
          { rel: "alternate", hrefLang: "en", href: enHref },
          { rel: "alternate", hrefLang: "en-BD", href: enHref },
          { rel: "alternate", hrefLang: "en-US", href: enHref },
          { rel: "alternate", hrefLang: "bn", href: bnHref },
          { rel: "alternate", hrefLang: "bn-BD", href: bnHref },
          { rel: "alternate", hrefLang: "x-default", href: enHref },
        ]
      : []),
  ];

  return { meta, links, url, image };
}

/**
 * Mirrored hreflang set for Bangla routes. Pass the Bangla URL/path and, when a
 * real English counterpart exists, its URL/path. Without an English counterpart
 * the page only self-references (no cross-language claim we can't back up).
 */
export function bnHreflangLinks(bnPath: string, enPath?: string) {
  const toAbs = (p: string) =>
    p.startsWith("http")
      ? p
      : p === "/"
        ? SITE.url
        : `${SITE.url}${p.startsWith("/") ? "" : "/"}${p}`;
  const bnHref = toAbs(bnPath);
  const links: Array<{ rel: string; hrefLang?: string; href: string }> = [
    { rel: "canonical", href: bnHref },
  ];
  if (enPath) {
    const enHref = toAbs(enPath);
    links.push(
      { rel: "alternate", hrefLang: "bn", href: bnHref },
      { rel: "alternate", hrefLang: "bn-BD", href: bnHref },
      { rel: "alternate", hrefLang: "en", href: enHref },
      { rel: "alternate", hrefLang: "en-BD", href: enHref },
      { rel: "alternate", hrefLang: "en-US", href: enHref },
      { rel: "alternate", hrefLang: "x-default", href: enHref }
    );
  }
  return links;
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE.url}${it.path === "/" ? "" : it.path}`,
    })),
  };
}
